import { Bonus, BonusThresholdMetric } from '@/types/api/bonus';

/**
 * Same estimate as the website's profit calculator (web: src/config/profit.ts
 * and src/utils/profit.ts), so the amount a leader saw there is the amount
 * they see here. Keep both in sync.
 */
const EXAMPLE_PRICE_PER_ITEM = 160;

/**
 * Profit rate in basis points (5000 = 50 %) from the given item count and up.
 * Also the API's profit tiers, shown as steps on the sale's meter.
 */
export const PROFIT_RATE_STEPS = [
  { itemCount: 200, profitRate: 5000 },
  { itemCount: 100, profitRate: 4500 },
  { itemCount: 0, profitRate: 4000 },
];

function getProfitRate(itemCount: number): number {
  const steps = [...PROFIT_RATE_STEPS].sort((a, b) => b.itemCount - a.itemCount);

  return (steps.find((step) => itemCount >= step.itemCount) ?? steps[steps.length - 1]).profitRate;
}

/**
 * Estimated profit in kr for a sale with the given participants and items per participant.
 * A bonus counts like in the API: when its threshold is reached, with the rate at most 100 %
 * and the profit at most the revenue.
 */
export function estimateProfit(participantCount: number, itemsPerParticipant: number, bonus?: Bonus | null): number {
  const itemCount = participantCount * itemsPerParticipant;
  const revenue = itemCount * EXAMPLE_PRICE_PER_ITEM;
  const baseRate = getProfitRate(itemCount) / 100;

  if (!bonus || !reachesThreshold(bonus, itemCount, revenue)) {
    return Math.round(revenue * (baseRate / 100));
  }

  const rate = Math.min(100, baseRate + (bonus.rate ?? 0));
  const fixed = (bonus.amount?.amount ?? 0) / 100;

  return Math.round(Math.min(revenue, revenue * (rate / 100) + fixed));
}

/** `revenue` in kr. */
function reachesThreshold({ threshold }: Bonus, itemCount: number, revenue: number) {
  if (!threshold) return true;

  return threshold.metric === BonusThresholdMetric.ItemCount
    ? itemCount >= threshold.value
    : revenue * 100 >= threshold.value;
}
